/**
 * Copyright (c) HashiCorp, Inc.
 * SPDX-License-Identifier: BUSL-1.1
 */

import Component from '@glimmer/component';
import {
  HdsCardContainer,
  HdsTextBody,
  HdsBadge,
  HdsApplicationState,
} from '@hashicorp/design-system-components/components';
import type { HdsApplicationStateSignature } from '@hashicorp/design-system-components/components/hds/application-state/index';
import TitleRow from './base/title-row.gts';
import { htmlSafe } from '@ember/template';

import './secrets-sync.scss';

export interface SecretsSyncSignature {
  Args: {
    totalDestinations: number;
    destinations: Record<string, number>;
  };

  Blocks: {
    default: [];
    /** We optionally yield application state to allow for overrides on empty state eg:
     * <SSUReportingSecretsSync ...>
     *   <:empty as |A|>
     *     <A.Header @title="Custom Title" />
     *     <A.Body @text="Custom description" />
     *   </:empty>
     * </SSUReportingSecretsSync>
     * */
    empty: HdsApplicationStateSignature['Blocks']['default'];
  };

  Element: HTMLElement;
}

export default class SecretsSync extends Component<SecretsSyncSignature> {
  get hasData() {
    return (
      this.args.totalDestinations > 0 &&
      Object.keys(this.args.destinations || {}).length > 0
    );
  }

  get description() {
    if (this.hasData) {
      return htmlSafe(
        'Total number of destinations (e.g. third-party integrations) synced with secrets ',
      );
    }
  }

  get linkUrl() {
    if (this.hasData) {
      return '/sync/secrets/overview';
    }
  }

  get totalDestinations() {
    return this.args.totalDestinations || 0;
  }

  get destinationsList() {
    const destinations = this.args.destinations || {};
    return Object.entries(destinations).map(([name, count]) => ({
      name,
      count,
    }));
  }

  <template>
    <HdsCardContainer
      data-test-vault-reporting-secrets-sync
      @hasBorder={{true}}
      class="ssu-secrets-sync"
      ...attributes
    >
      <TitleRow
        @title="Secrets sync"
        @description={{this.description}}
        @linkUrl={{this.linkUrl}}
      />

      {{#if this.hasData}}
        <HdsTextBody
          @size="300"
          data-test-vault-reporting-secrets-sync-destinations-row
        >
          {{this.totalDestinations}}
          Destinations
          {{#each this.destinationsList as |destination|}}
            <HdsBadge
              class="ssu-secrets-sync__badge"
              data-test-vault-reporting-secrets-sync-destinations-badge
              @type="outlined"
              @size="small"
              @text="{{destination.name}}: {{destination.count}}"
            />
          {{/each}}
        </HdsTextBody>

      {{else}}
        <HdsApplicationState
          data-test-vault-reporting-secrets-sync-empty-state
          class="ssu-secrets-sync__empty-state"
          as |A|
        >
          {{#if (has-block "empty")}}
            {{yield A to="empty"}}
          {{else}}
            <A.Body
              data-test-vault-reporting-secrets-sync-empty-state-description
              @text="An Enterprise feature that automatically replicates secrets to third-party integrations. It is recommended so organizations can remain consistent with their existing operations."
            />

            <A.Footer as |F|>
              <F.LinkStandalone
                data-test-vault-reporting-secrets-sync-empty-state-link
                @icon="docs-link"
                @iconPosition="trailing"
                @text="Secrets sync"
                @href="https://developer.hashicorp.com/vault/docs/sync"
                target="_blank"
              />
            </A.Footer>
          {{/if}}
        </HdsApplicationState>
      {{/if}}

    </HdsCardContainer>
  </template>
}
